<template>
  <div class="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
    
    <!-- Top Alert Notifications -->
    <div v-if="notification" :class="`mb-6 p-4 rounded-xl flex items-center justify-between border ${notification.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`">
      <div class="flex items-center gap-2">
        <i :class="notification.type === 'success' ? 'fa-solid fa-circle-check text-emerald-500' : 'fa-solid fa-circle-exclamation text-rose-500'"></i>
        <span class="text-sm font-medium">{{ notification.message }}</span>
      </div>
      <button @click="notification = null" class="text-xs font-bold hover:opacity-70">&times;</button>
    </div>

    <!-- Overview Section -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
      <div>
        <h1 class="text-3xl font-extrabold tracking-tight text-gray-900">Search Console Dashboard</h1>
        <p class="mt-1 text-sm text-gray-500">Connect Google Search Console accounts, manage verified properties, and monitor performance snapshots.</p>
      </div>
      <div>
        <a href="/auth/google" class="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] transition-all text-white font-semibold rounded-lg shadow-sm text-sm">
          <i class="fa-solid fa-plus"></i> Connect Google Account
        </a>
      </div>
    </div>

    <!-- Active Property View -->
    <div v-if="selectedProperty" class="mb-10 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <!-- Property header -->
      <div class="px-6 py-5 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-lg">
            <i class="fa-solid fa-globe"></i>
          </div>
          <div>
            <div class="flex items-center gap-2">
              <h2 class="text-lg font-bold text-gray-900">{{ displaySiteUrl(selectedProperty.site_url) }}</h2>
              <span class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                {{ selectedProperty.permission_level }}
              </span>
            </div>
            <p class="text-xs text-gray-500 mt-0.5">Connected account: {{ getAccountEmail(selectedProperty.google_account_id) }}</p>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-3">
          <!-- Privacy Mode Selector -->
          <div class="flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200">
            <button 
              @click="setPrivacyMode('public_domain')"
              :class="`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${selectedProperty.privacy_mode === 'public_domain' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`"
            >
              Show Domain
            </button>
            <button 
              @click="setPrivacyMode('hidden_domain')"
              :class="`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${selectedProperty.privacy_mode === 'hidden_domain' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`"
            >
              Blur Domain
            </button>
          </div>

          <!-- Sync button -->
          <button 
            @click="syncMetrics" 
            :disabled="syncing"
            class="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white border border-gray-300 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <i :class="`fa-solid fa-arrows-rotate ${syncing ? 'animate-spin' : ''}`"></i>
            {{ syncing ? 'Syncing...' : 'Sync Now' }}
          </button>
        </div>
      </div>

      <!-- Stats display grid -->
      <div class="p-6">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <!-- Clicks Card -->
          <div class="bg-gray-50 p-5 rounded-xl border border-gray-200">
            <span class="text-xs text-gray-400 font-bold uppercase tracking-wider">Clicks (Last 30 Days)</span>
            <div class="text-3xl font-extrabold text-indigo-600 mt-2">
              {{ formatNumber(snapshot ? snapshot.clicks : 0) }}
            </div>
            <p class="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <i class="fa-solid fa-circle-check text-indigo-500"></i> Verified GSC Data
            </p>
          </div>

          <!-- Impressions Card -->
          <div class="bg-gray-50 p-5 rounded-xl border border-gray-200">
            <span class="text-xs text-gray-400 font-bold uppercase tracking-wider">Impressions</span>
            <div class="text-3xl font-extrabold text-blue-600 mt-2">
              {{ formatNumber(snapshot ? snapshot.impressions : 0) }}
            </div>
            <p class="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <i class="fa-solid fa-circle-check text-blue-500"></i> Verified GSC Data
            </p>
          </div>

          <!-- CTR Card -->
          <div class="bg-gray-50 p-5 rounded-xl border border-gray-200">
            <span class="text-xs text-gray-400 font-bold uppercase tracking-wider">Average CTR</span>
            <div class="text-3xl font-extrabold text-emerald-600 mt-2">
              {{ snapshot ? snapshot.ctr : '0.00' }}%
            </div>
            <p class="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <i class="fa-solid fa-circle-check text-emerald-500"></i> Verified GSC Data
            </p>
          </div>

          <!-- Position Card -->
          <div class="bg-gray-50 p-5 rounded-xl border border-gray-200">
            <span class="text-xs text-gray-400 font-bold uppercase tracking-wider">Avg. Position</span>
            <div class="text-3xl font-extrabold text-amber-600 mt-2">
              {{ snapshot ? snapshot.avg_position : '0.0' }}
            </div>
            <p class="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <i class="fa-solid fa-circle-check text-amber-500"></i> Verified GSC Data
            </p>
          </div>
        </div>

        <!-- Sync Date Info -->
        <div class="mt-6 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-4 gap-2">
          <span>Snapshot timestamped: <b>{{ snapshot ? formatDate(snapshot.updated_at) : 'Never synced' }}</b></span>
          <span class="bg-amber-50 text-amber-800 border border-amber-200 rounded px-2.5 py-0.5 font-medium flex items-center gap-1">
            <i class="fa-solid fa-clock-rotate-left"></i> GSC metric sync includes a 3-day data delay
          </span>
        </div>
      </div>
    </div>

    <!-- Connected Accounts and Properties Selector -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
      
      <!-- Accounts Panel -->
      <div class="lg:col-span-1 bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h3 class="text-lg font-bold text-gray-900 mb-4">Google Connections</h3>
        
        <div v-if="accounts.length === 0" class="text-center py-6">
          <i class="fa-brands fa-google text-3xl text-gray-300 mb-2 block"></i>
          <p class="text-sm text-gray-500">No Google accounts connected.</p>
        </div>

        <div v-else class="space-y-4">
          <div v-for="account in accounts" :key="account.id" class="border border-gray-100 rounded-xl p-4 bg-gray-50/50">
            <div class="flex items-start justify-between gap-2">
              <div class="truncate">
                <p class="text-sm font-bold text-gray-900 truncate">{{ account.google_email }}</p>
                <span class="inline-flex text-[10px] text-gray-400 font-medium">Connected {{ formatDate(account.created_at) }}</span>
              </div>
              <button @click="disconnectAccount(account.id)" class="text-gray-400 hover:text-red-500 p-1" title="Disconnect Account">
                <i class="fa-solid fa-trash-can text-sm"></i>
              </button>
            </div>

            <!-- List properties action button -->
            <button 
              @click="loadProperties(account)" 
              :disabled="loadingPropertiesAccount === account.id"
              class="w-full mt-3 text-center py-1.5 border border-indigo-200 bg-indigo-50/50 hover:bg-indigo-50 text-indigo-700 font-bold rounded-lg text-xs transition-colors"
            >
              {{ loadingPropertiesAccount === account.id ? 'Loading sites...' : 'Manage Properties' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Properties Selector Panel -->
      <div class="lg:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h3 class="text-lg font-bold text-gray-900 mb-2">Property Selector</h3>
        <p class="text-xs text-gray-500 mb-6">Select a site property to fetch and verify Search Console data.</p>

        <div v-if="!activeAccount" class="flex flex-col items-center justify-center py-12 text-center">
          <div class="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 border border-gray-100 mb-3">
            <i class="fa-solid fa-list-check"></i>
          </div>
          <p class="text-sm font-semibold text-gray-600">Select a Google connection to view properties</p>
          <p class="text-xs text-gray-400 max-w-xs mt-1">Click "Manage Properties" on one of your connected Google accounts on the left to see the sites.</p>
        </div>

        <div v-else>
          <div class="flex items-center justify-between border-b border-gray-150 pb-3 mb-4">
            <span class="text-sm font-bold text-gray-800">Sites for {{ activeAccount.google_email }}</span>
            <span class="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">{{ properties.length }} sites</span>
          </div>

          <div v-if="properties.length === 0" class="text-center py-8 text-sm text-gray-400">
            No Search Console properties found for this Google account.
          </div>

          <div v-else class="space-y-2 max-h-96 overflow-y-auto pr-1">
            <div 
              v-for="prop in properties" 
              :key="prop.siteUrl"
              class="flex items-center justify-between p-3.5 border border-gray-100 rounded-xl hover:bg-gray-50/70 transition-all gap-4"
            >
              <div class="truncate">
                <p class="text-sm font-bold text-gray-900 truncate" :title="prop.siteUrl">{{ displaySiteUrl(prop.siteUrl) }}</p>
                <p class="text-[10px] text-gray-400 tracking-wide font-mono truncate mt-0.5">{{ prop.siteUrl }}</p>
              </div>

              <div class="flex items-center gap-3">
                <span class="text-xs bg-gray-50 border border-gray-150 text-gray-600 px-2 py-0.5 rounded">
                  {{ prop.permissionLevel }}
                </span>
                
                <button 
                  @click="selectPropertyForDashboard(prop)"
                  :disabled="selectingProperty"
                  :class="`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${isPropertyActive(prop.siteUrl) ? 'bg-indigo-50 border border-indigo-200 text-indigo-700 cursor-default' : 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-[0.97]'}`"
                >
                  {{ isPropertyActive(prop.siteUrl) ? 'Active' : 'Select' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DashboardManager',
  props: {
    initialAccounts: {
      type: Array,
      default: () => []
    },
    initialSelectedProperty: {
      type: Object,
      default: null
    },
    initialSnapshot: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      accounts: this.initialAccounts,
      selectedProperty: this.initialSelectedProperty,
      snapshot: this.initialSnapshot,
      activeAccount: null,
      properties: [],
      loadingPropertiesAccount: null,
      selectingProperty: false,
      syncing: false,
      notification: null
    };
  },
  methods: {
    getAccountEmail(accountId) {
      const acc = this.accounts.find(a => a.id === accountId);
      return acc ? acc.google_email : 'Unknown Account';
    },
    displaySiteUrl(url) {
      if (!url) return '';
      // Strip protocol and trailing slash for prettier UI
      return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
    },
    isPropertyActive(siteUrl) {
      return this.selectedProperty && this.selectedProperty.site_url === siteUrl;
    },
    formatNumber(num) {
      if (num === null || num === undefined) return '0';
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
      return num.toLocaleString();
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    showNotice(message, type = 'success') {
      this.notification = { message, type };
      setTimeout(() => {
        this.notification = null;
      }, 5000);
    },
    async loadProperties(account) {
      this.activeAccount = account;
      this.loadingPropertiesAccount = account.id;
      this.properties = [];
      try {
        const response = await axios.get(`/properties/${account.id}`);
        this.properties = response.data.properties || [];
      } catch (err) {
        this.showNotice('Failed to fetch Search Console properties. Try reconnecting.', 'error');
      } finally {
        this.loadingPropertiesAccount = null;
      }
    },
    async selectPropertyForDashboard(prop) {
      if (this.isPropertyActive(prop.siteUrl)) return;
      this.selectingProperty = true;
      try {
        const response = await axios.post('/properties/select', {
          google_account_id: this.activeAccount.id,
          site_url: prop.siteUrl,
          permission_level: prop.permissionLevel
        });
        if (response.data.success) {
          window.location.reload();
        }
      } catch (err) {
        this.showNotice('Failed to select property. Check GSC permission level.', 'error');
      } finally {
        this.selectingProperty = false;
      }
    },
    async setPrivacyMode(mode) {
      if (!this.selectedProperty) return;
      try {
        const response = await axios.post(`/properties/${this.selectedProperty.id}/privacy`, {
          privacy_mode: mode
        });
        if (response.data.success) {
          this.selectedProperty.privacy_mode = mode;
          this.showNotice('Privacy mode updated to: ' + (mode === 'public_domain' ? 'Show Domain' : 'Blur Domain'));
        }
      } catch (err) {
        this.showNotice('Failed to update privacy settings.', 'error');
      }
    },
    async syncMetrics() {
      if (!this.selectedProperty) return;
      this.syncing = true;
      try {
        const response = await axios.post(`/properties/${this.selectedProperty.id}/sync`);
        if (response.data.success) {
          this.snapshot = response.data.snapshot;
          this.showNotice('Metrics synchronized with Google Search Console.');
        }
      } catch (err) {
        this.showNotice('Failed to query metrics. Please ensure account is connected.', 'error');
      } finally {
        this.syncing = false;
      }
    },
    async disconnectAccount(accountId) {
      if (confirm('Disconnect this Google account? All associated properties and metric snapshots will be deleted.')) {
        try {
          // Trigger standard delete form request
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = `/auth/google/${accountId}`;
          
          const csrfInput = document.createElement('input');
          csrfInput.type = 'hidden';
          csrfInput.name = '_token';
          csrfInput.value = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
          
          const methodInput = document.createElement('input');
          methodInput.type = 'hidden';
          methodInput.name = '_method';
          methodInput.value = 'DELETE';
          
          form.appendChild(csrfInput);
          form.appendChild(methodInput);
          document.body.appendChild(form);
          form.submit();
        } catch (err) {
          this.showNotice('Failed to disconnect Google connection.', 'error');
        }
      }
    }
  }
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 4px;
}
</style>
